import React, { useState } from 'react'
import {
  Container, Row, Col, Card, CardBody, FormGroup, Label, Input,
  Button, Spinner, Alert, CustomInput,
} from 'reactstrap'
import DatePicker from 'react-datepicker'
import logo from '../../../assets/images/logo-light.png'
import AsyncSelect from '../../../components/Template/Input/AsyncSelect'
import { notifyError, notifySuccess } from '../../../utils/notify'
import { searchPfByName, submitFichaPj } from './fichaPjApi'

const PORTE_OPTIONS = [
  { value: 'LTDA', label: 'Limitada – LTDA' },
  { value: 'ME', label: 'ME' },
  { value: 'EPP', label: 'EPP' },
  { value: 'MEI', label: 'MEI' },
]

const RAMO_OPTIONS = [
  { value: 'Comercio', label: 'Comércio' },
  { value: 'Industria', label: 'Indústria' },
  { value: 'Servicos', label: 'Serviços' },
]

const ESTRUTURA_OPTIONS = [
  'Computador/notebook',
  'Celular/Tablet',
  'Equipamentos técnicos (GPS, Binóculos, Armadilhas e/ou similares)',
  'Veículo próprio',
  'Equipe',
  'Sede/sala de atendimento',
  'Não se aplica',
]

const DOCUMENTO_OPTIONS = [
  'Contrato social e demais alterações',
  'CNPJ/MEI',
  'CPF dos representantes legais da empresa',
  'Inscrição e situação cadastral atual junto a Receita Federal do Brasil',
  'Certidão Negativa de Débitos Municipais, Estadual e Federal',
]

const EMPTY_FORM = {
  razaoSocial: '',
  cnpj: '',
  nomeFantasia: '',
  dtAbertura: null,
  inscMunicipal: '',
  inscEstadual: '',
  inscEstadualIsento: false,
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  telefone: '',
  celular: '',
  telComercial: '',
  email: '',
  obs: '',
  tipoServico: '',
  porte: '',
  ramo: '',
  optanteSimples: '',
  banco: '',
  agencia: '',
  contaNum: '',
  contaTp: '',
  estrutura: [],
  possuiContador: '',
  possuiColaboradores: '',
  faturamentoMedio: '',
  numClientes: '',
  repNome: '',
  repDtNasc: null,
  repCpf: '',
  repFormacao: '',
  repCelular: '',
  repWhatsapp: '',
  repEndereco: '',
  repCidade: '',
  repBairro: '',
  repUf: '',
  repCep: '',
  repEmail: '',
  repTelefone: '',
  documentos: ['CNPJ/MEI', 'CPF dos representantes legais da empresa'],
  declaracaoVerdadeira: false,
  repAssinaturaNome: '',
}

const sectionTitleStyle = {
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#273351',
  marginTop: 24,
  marginBottom: 12,
  paddingBottom: 6,
  borderBottom: '2px solid #e9ecef',
}

const BoolRadio = ({ name, value, onChange, label }) => (
  <FormGroup className="mb-2">
    <Label className="d-block">{label}</Label>
    <div>
      <CustomInput
        type="radio"
        id={`${name}-sim`}
        name={name}
        label="Sim"
        checked={value === true}
        onChange={() => onChange(true)}
        inline
      />
      <CustomInput
        type="radio"
        id={`${name}-nao`}
        name={name}
        label="Não"
        checked={value === false}
        onChange={() => onChange(false)}
        inline
      />
    </div>
  </FormGroup>
)

const FichaPj = () => {
  const [selectedPf, setSelectedPf] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const toggleArrayItem = (field, item) => {
    setForm((prev) => {
      const arr = prev[field] || []
      const next = arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item]
      return { ...prev, [field]: next }
    })
  }

  const prefillFromPf = (pf) => {
    if (!pf) return
    setForm((prev) => ({
      ...prev,
      repNome: pf.pf_nomeExibicao || prev.repNome,
      repCpf: pf.pf_cpf ? String(pf.pf_cpf) : prev.repCpf,
      repDtNasc: pf.pf_dtNasc ? new Date(pf.pf_dtNasc) : prev.repDtNasc,
      repCelular: pf.pf_celProf ? String(pf.pf_celProf) : prev.repCelular,
      repEmail: pf.pf_emailProf || prev.repEmail,
      repTelefone: pf.pf_telFixoProf || prev.repTelefone,
      repEndereco: pf.pf_endLogr || prev.repEndereco,
      repCep: pf.pf_cep ? String(pf.pf_cep) : prev.repCep,
      banco: pf.pf_contaBanco || prev.banco,
      agencia: pf.pf_contaAgencia || prev.agencia,
      contaNum: pf.pf_contaNum || prev.contaNum,
      contaTp: pf.pf_contaTp || prev.contaTp,
      repAssinaturaNome: pf.pf_nomeExibicao || prev.repAssinaturaNome,
    }))
  }

  const handlePfChange = (option) => {
    setSelectedPf(option)
    setSubmitted(false)
    if (option) {
      prefillFromPf(option)
    } else {
      setForm(EMPTY_FORM)
    }
  }

  const validate = () => {
    if (!selectedPf?.pf_id) {
      notifyError('Selecione o colaborador responsável pelo preenchimento.')
      return false
    }
    if (!form.razaoSocial?.trim()) {
      notifyError('Informe a Razão Social.')
      return false
    }
    if (!form.cnpj?.trim()) {
      notifyError('Informe o CNPJ.')
      return false
    }
    if (!form.repNome?.trim()) {
      notifyError('Informe o nome do representante legal.')
      return false
    }
    if (!form.declaracaoVerdadeira) {
      notifyError('É necessário aceitar a declaração de veracidade das informações.')
      return false
    }
    if (!form.repAssinaturaNome?.trim()) {
      notifyError('Informe o nome para assinatura do representante legal.')
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    try {
      const usuarioCria = selectedPf.pf_id
      await submitFichaPj(selectedPf.pf_id, form, usuarioCria)
      setSubmitted(true)
      notifySuccess('Ficha cadastral enviada com sucesso! E-mails enviados para o RH e para o colaborador selecionado.')
    } catch (err) {
      notifyError(err?.message || 'Erro ao enviar a ficha cadastral.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f4f6f9', padding: '24px 0 48px' }}>
      <Container fluid style={{ maxWidth: 1100 }}>
        <div
          className="text-center mb-4"
          style={{
            backgroundColor: '#273351',
            borderRadius: 8,
            padding: '20px 16px',
          }}
        >
          <img src={logo} width={180} height={40} alt="Ecossis Soluções Ambientais" style={{ marginBottom: 12 }} />
          <h4 className="text-white mb-1" style={{ fontSize: '1.15rem' }}>Cadastro de Prestador de Serviços — PJ</h4>
          <p className="text-white-50 mb-0" style={{ fontSize: '0.85rem' }}>
            Preencha a ficha cadastral conforme os dados da sua empresa.
          </p>
        </div>

        <Card>
          <CardBody>
            <h5 className="mb-3">Seleção do colaborador</h5>
            <FormGroup>
              <Label>Buscar colaborador por nome</Label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                isClearable
                placeholder="Digite o nome para buscar..."
                getOptionLabel={(o) => `${o.pf_nomeExibicao} (ID: ${o.pf_id})`}
                getOptionValue={(o) => String(o.pf_id)}
                value={selectedPf}
                onChange={handlePfChange}
                loadOptions={async (inputValue) => searchPfByName(inputValue)}
              />
            </FormGroup>

            {selectedPf && (
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                Colaborador selecionado: <strong>{selectedPf.pf_nomeExibicao}</strong>
                {selectedPf.pf_emailProf ? ` — ${selectedPf.pf_emailProf}` : ''}
              </p>
            )}
          </CardBody>
        </Card>

        {selectedPf && !submitted && (
          <Card className="mt-3">
            <CardBody>
              <h5 className="mb-1">Cadastro de Prestador de Serviços</h5>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                Preencha conforme a ficha cadastral PJ. Os dados serão salvos e enviados ao RH.
              </p>

              <div style={sectionTitleStyle}>Dados cadastrais</div>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Razão Social *</Label>
                    <Input value={form.razaoSocial} onChange={(e) => setField('razaoSocial', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>CNPJ *</Label>
                    <Input value={form.cnpj} onChange={(e) => setField('cnpj', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Data de abertura</Label>
                    <DatePicker
                      locale="pt-BR"
                      className="form-control"
                      selected={form.dtAbertura}
                      dateFormat="dd/MM/yyyy"
                      onChange={(d) => setField('dtAbertura', d)}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Nome Fantasia</Label>
                    <Input value={form.nomeFantasia} onChange={(e) => setField('nomeFantasia', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Inscrição Municipal</Label>
                    <Input value={form.inscMunicipal} onChange={(e) => setField('inscMunicipal', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Inscrição Estadual</Label>
                    <Input
                      value={form.inscEstadual}
                      disabled={form.inscEstadualIsento}
                      onChange={(e) => setField('inscEstadual', e.target.value)}
                    />
                    <CustomInput
                      type="checkbox"
                      id="insc-est-isento"
                      className="mt-1"
                      label="Isento"
                      checked={form.inscEstadualIsento}
                      onChange={(e) => {
                        setField('inscEstadualIsento', e.target.checked)
                        if (e.target.checked) setField('inscEstadual', '')
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div style={sectionTitleStyle}>Contato</div>
              <Row>
                <Col md={5}>
                  <FormGroup>
                    <Label>Endereço da empresa</Label>
                    <Input value={form.endereco} onChange={(e) => setField('endereco', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>Número</Label>
                    <Input value={form.numero} onChange={(e) => setField('numero', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>Complemento</Label>
                    <Input value={form.complemento} onChange={(e) => setField('complemento', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Bairro</Label>
                    <Input value={form.bairro} onChange={(e) => setField('bairro', e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Cidade</Label>
                    <Input value={form.cidade} onChange={(e) => setField('cidade', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>Estado</Label>
                    <Input value={form.estado} maxLength={2} onChange={(e) => setField('estado', e.target.value.toUpperCase())} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>CEP</Label>
                    <Input value={form.cep} onChange={(e) => setField('cep', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>E-mail</Label>
                    <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Telefone</Label>
                    <Input value={form.telefone} onChange={(e) => setField('telefone', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Celular</Label>
                    <Input value={form.celular} onChange={(e) => setField('celular', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Telefone comercial</Label>
                    <Input value={form.telComercial} onChange={(e) => setField('telComercial', e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Observação</Label>
                <Input type="textarea" rows={2} value={form.obs} onChange={(e) => setField('obs', e.target.value)} />
              </FormGroup>

              <FormGroup>
                <Label>Tipo de serviço a ser prestado</Label>
                <Input type="textarea" rows={3} value={form.tipoServico} onChange={(e) => setField('tipoServico', e.target.value)} />
              </FormGroup>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Porte da empresa</Label>
                    <Input type="select" value={form.porte} onChange={(e) => setField('porte', e.target.value)}>
                      <option value="">Selecionar...</option>
                      {PORTE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Ramo de atividade</Label>
                    <Input type="select" value={form.ramo} onChange={(e) => setField('ramo', e.target.value)}>
                      <option value="">Selecionar...</option>
                      {RAMO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <BoolRadio
                    name="optanteSimples"
                    label="Optante pelo Simples Nacional"
                    value={form.optanteSimples === '' ? null : form.optanteSimples}
                    onChange={(v) => setField('optanteSimples', v)}
                  />
                </Col>
              </Row>

              <div style={sectionTitleStyle}>Dados bancários</div>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Banco</Label>
                    <Input value={form.banco} onChange={(e) => setField('banco', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Agência</Label>
                    <Input value={form.agencia} onChange={(e) => setField('agencia', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Número da conta</Label>
                    <Input value={form.contaNum} onChange={(e) => setField('contaNum', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Tipo da conta</Label>
                    <Input type="select" value={form.contaTp} onChange={(e) => setField('contaTp', e.target.value)}>
                      <option value="">Selecionar...</option>
                      <option value="Corrente">Corrente</option>
                      <option value="Poupanca">Poupança</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <div style={sectionTitleStyle}>Estrutura da empresa alocada para o contrato</div>
              <Row>
                {ESTRUTURA_OPTIONS.map((item) => (
                  <Col md={6} key={item}>
                    <CustomInput
                      type="checkbox"
                      id={`estrutura-${item}`}
                      label={item}
                      checked={form.estrutura.includes(item)}
                      onChange={() => toggleArrayItem('estrutura', item)}
                      className="mb-2"
                    />
                  </Col>
                ))}
              </Row>

              <div style={sectionTitleStyle}>Informações complementares</div>
              <Row>
                <Col md={4}>
                  <BoolRadio
                    name="possuiContador"
                    label="Possui contador responsável pela empresa?"
                    value={form.possuiContador === '' ? null : form.possuiContador}
                    onChange={(v) => setField('possuiContador', v)}
                  />
                </Col>
                <Col md={4}>
                  <BoolRadio
                    name="possuiColaboradores"
                    label="Possui colaboradores, sócios ou terceiros que executam atividades em seu nome?"
                    value={form.possuiColaboradores === '' ? null : form.possuiColaboradores}
                    onChange={(v) => setField('possuiColaboradores', v)}
                  />
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Faturamento médio mensal (R$)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.faturamentoMedio}
                      onChange={(e) => setField('faturamentoMedio', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Nº de clientes ativos</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.numClientes}
                      onChange={(e) => setField('numClientes', e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div style={sectionTitleStyle}>Dados do representante legal da empresa</div>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Nome *</Label>
                    <Input value={form.repNome} onChange={(e) => setField('repNome', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Data de nascimento</Label>
                    <DatePicker
                      locale="pt-BR"
                      className="form-control"
                      selected={form.repDtNasc}
                      dateFormat="dd/MM/yyyy"
                      onChange={(d) => setField('repDtNasc', d)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>CPF</Label>
                    <Input value={form.repCpf} onChange={(e) => setField('repCpf', e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Formação</Label>
                    <Input value={form.repFormacao} onChange={(e) => setField('repFormacao', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Celular</Label>
                    <Input value={form.repCelular} onChange={(e) => setField('repCelular', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>WhatsApp</Label>
                    <Input value={form.repWhatsapp} onChange={(e) => setField('repWhatsapp', e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Endereço</Label>
                    <Input value={form.repEndereco} onChange={(e) => setField('repEndereco', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Bairro</Label>
                    <Input value={form.repBairro} onChange={(e) => setField('repBairro', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Cidade / UF</Label>
                    <div className="d-flex" style={{ gap: 8 }}>
                      <Input value={form.repCidade} onChange={(e) => setField('repCidade', e.target.value)} />
                      <Input style={{ maxWidth: 60 }} maxLength={2} value={form.repUf} onChange={(e) => setField('repUf', e.target.value.toUpperCase())} />
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>CEP</Label>
                    <Input value={form.repCep} onChange={(e) => setField('repCep', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <Label>E-mail</Label>
                    <Input type="email" value={form.repEmail} onChange={(e) => setField('repEmail', e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Telefone</Label>
                    <Input value={form.repTelefone} onChange={(e) => setField('repTelefone', e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>

              <div style={sectionTitleStyle}>Documentos necessários para cadastro</div>
              <Row>
                {DOCUMENTO_OPTIONS.map((item) => (
                  <Col md={6} key={item}>
                    <CustomInput
                      type="checkbox"
                      id={`doc-${item}`}
                      label={item}
                      checked={form.documentos.includes(item)}
                      onChange={() => toggleArrayItem('documentos', item)}
                      className="mb-2"
                    />
                  </Col>
                ))}
              </Row>

              <Alert color="secondary" className="mt-3" style={{ fontSize: '0.85rem' }}>
                O preenchimento da ficha cadastral por si só não configura a celebração de contrato de prestador de serviço.
              </Alert>

              <div style={sectionTitleStyle}>Declaração</div>
              <CustomInput
                type="checkbox"
                id="declaracao-verdadeira"
                label="Declaro que, para os fins de direito, todas as informações prestadas neste formulário são verdadeiras. *"
                checked={form.declaracaoVerdadeira}
                onChange={(e) => setField('declaracaoVerdadeira', e.target.checked)}
                className="mb-3"
              />
              <FormGroup>
                <Label>Nome do representante legal (assinatura) *</Label>
                <Input value={form.repAssinaturaNome} onChange={(e) => setField('repAssinaturaNome', e.target.value)} />
              </FormGroup>

              <Button color="primary" disabled={submitting} onClick={handleSubmit}>
                Enviar ficha cadastral
                {submitting && <Spinner size="sm" className="ml-2" />}
              </Button>
            </CardBody>
          </Card>
        )}

        {submitted && (
          <Alert color="success" className="mt-3">
            Ficha cadastral registrada e enviada para <strong>rh@ecossis.com.br</strong>
            {selectedPf?.pf_emailProf ? ` e para ${selectedPf.pf_emailProf}` : ''}.
          </Alert>
        )}
      </Container>
    </div>
  )
}

export default FichaPj
